import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import {
  ArchiveIcon,
  ArchiveRestore,
  CircleDollarSign,
  CloudCog,
  Info,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ArchivedEntityAlert } from '@/components/archived-entity-alert';
import { ScheduledArchivationAlert } from '@/components/scheduled-archivation-alert';
import { StatusBadgeType, usePermissions } from '@/modules/common';
import { getProjectStatusText } from '@/modules/common/components/molecules/badge/status-configs/project-status-config';
import { EditChangeAlert } from '@/modules/common/components/molecules/edit-change-alert';
import { ScheduledChangeAlert } from '@/modules/common/components/molecules/scheduled-change-alert';
import {
  ActionButtonConfig,
  FeatureDetailsTemplate,
  FeatureMode,
  FeatureTab,
  useTabValidation,
} from '@/modules/common/components/templates';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { PatchEntityResult } from '@/modules/common/types/patch-entity-result';
import {
  ArchiveProjectModal,
  DeleteProjectsModal,
  ProjectBillingTab,
  ProjectBillingTabRef,
  ProjectTab,
  UnarchiveProjectModal,
  useProjectDetails,
} from '@/modules/projects';
import {
  ProjectInfoTab,
  ProjectInfoTabRef,
} from '@/modules/projects/pages/details/tab-pages/project-info-tab';
import {
  ProjectIntegrationsTab,
  ProjectIntegrationsTabRef,
} from '@/modules/projects/pages/details/tab-pages/project-integrations-tab';
import { Project, ProjectStatus } from '@/modules/projects/types/project';
import { ProjectChangeType } from '@/modules/projects/types/project-changes';
import { AuthenticatedRoutes } from '@/router/routes';
import {
  formatDateToNonISOFormat,
  formatDisplayDate,
  getTodayISOString,
} from '@/utils/date-formatters';

import { useProjectDependencies } from '../../hooks/info/use-project-dependencies';
import { usePatchProjectChange } from '../../hooks/use-patch-project-change';
import { useProjectChanges } from '../../hooks/use-project-changes';
import { useProjectDetailsByRevisionDate } from '../../hooks/use-project-details-by-revision-date';
import { useProjectDraft } from '../../hooks/use-project-draft';
import { ProjectBillingFormData, ProjectFormData } from '../../types';
import { ProjectChangesTab } from './tab-pages';

/**
 * Comprehensive project management page supporting create, edit, and read modes.
 * Uses the enhanced FeatureDetailsTemplate for streamlined form management.
 *
 * Features:
 * - Multi-mode operation (Create/Edit/Read) with role-based access control
 * - Tab-based form navigation with draft data persistence
 * - Unsaved changes detection and confirmation dialogs
 * - Project archiving/unarchiving and deletion capabilities
 * - Responsive design with mobile-optimized interactions
 * - Logo upload with optimistic UI updates
 * - Streamlined form validation and state management via template
 */
export function ProjectDetailsPage() {
  const { t } = useTranslation(['authenticated.projects', 'common']);
  const navigate = useNavigate();
  const { projectId, changeId } = useParams<{ projectId: string; changeId?: string }>();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const { hasPermission } = usePermissions();
  const canViewProjects = hasPermission(OpsiumPolicy.ProjectsViewAll);
  const isDeleteProjectsEnabled = hasPermission(OpsiumPolicy.ProjectsDelete);
  const isEditProjectsEnabled = hasPermission(OpsiumPolicy.ProjectsEdit);
  const isManageProjectBillingEnabled = hasPermission(OpsiumPolicy.ProjectsBillingManage);

  // Determine if in EditChange mode
  const isEditChangeMode = !!changeId;

  // Tab refs for form management
  const projectInfoFormRef = useRef<ProjectInfoTabRef>(null);
  const projectBillingFormRef = useRef<ProjectBillingTabRef>(null);
  const projectIntegrationsFormRef = useRef<ProjectIntegrationsTabRef>(null);

  // Ref to hold allowNavigation function from unsaved changes guard
  // Used to bypass guard after successful save operations in EditChange mode
  const allowNavigationRef = useRef<(() => void) | null>(null);

  // Modal state
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isArchivedRangesAlertOpen, setIsArchivedRangesAlertOpen] = useState(false);
  const [archivedRangesMessage, setArchivedRangesMessage] = useState('');

  // Form state change handler for the template
  const [formStateChangeHandler, setFormStateChangeHandler] = useState<
    | ((
        isDirty: boolean,
        hasChanges?: boolean,
        isValid?: boolean,
        hasOnlyNonVersionedChanges?: boolean
      ) => void)
    | null
  >(null);

  // Track whether only non-versioned fields changed (for disabling date picker)
  const [hasOnlyNonVersionedChanges, setHasOnlyNonVersionedChanges] = useState(false);

  // Fallback handler for form state changes when template handler is not yet ready
  // Tracks hasOnlyNonVersionedChanges as local state
  const fallbackFormStateChangeHandler = useCallback(
    (_isDirty: boolean, _hasChanges?: boolean, _isValid?: boolean, onlyNonVersioned?: boolean) => {
      setHasOnlyNonVersionedChanges(onlyNonVersioned ?? false);
    },
    []
  );

  const {
    projectData,
    rawIntegrations,
    mode: originalMode,
    currentTab: originalCurrentTab,
    isLoading,
    isLoadingProject,
    isUploadingLogo,
    error,
    handleSaveProject,
    handleTabChange,
    handleLogoUpload,
  } = useProjectDetails();

  // Fetch project changes — used for EditChange metadata and scheduled change alerts on tabs.
  // Always enabled so the changes tab can reuse the cached data (same query key with empty filters).
  const { data: changesData } = useProjectChanges({
    projectId: Number(projectId),
    filters: {},
    enabled: !!projectId && projectId !== 'new',
  });

  // changeId is only unique WITHIN a section on the backend (separate id sequence per
  // section), so it can collide across sections — most notably for the initial
  // project-creation change, which writes an Info row and a Billing row in the same
  // transaction. The edit link carries `changeType` to disambiguate; fall back to an
  // id-only match for any older/external link that doesn't have it.
  const changeTypeParam = searchParams.get('changeType') as ProjectChangeType | null;
  const changeToEdit = useMemo(() => {
    if (!isEditChangeMode || !changesData?.items) return undefined;
    return (
      changesData.items.find(c => c.id === Number(changeId) && c.type === changeTypeParam) ??
      changesData.items.find(c => c.id === Number(changeId))
    );
  }, [isEditChangeMode, changesData, changeId, changeTypeParam]);

  // Derive next scheduled change per type from already-fetched changes data
  const nextChangeByType = useMemo(() => {
    if (!changesData?.items?.length || isEditChangeMode) return {};

    const todayStr = getTodayISOString().slice(0, 10);

    const result: Partial<
      Record<ProjectChangeType, { type: ProjectChangeType; startDate: string; id: number }>
    > = {};

    for (const change of changesData.items) {
      const dateStr = change.startDate.slice(0, 10);
      if (dateStr > todayStr) {
        const existing = result[change.type];
        if (!existing || dateStr < existing.startDate.slice(0, 10)) {
          result[change.type] = {
            type: change.type,
            startDate: change.startDate,
            id: change.id,
          };
        }
      }
    }

    return result;
  }, [changesData, isEditChangeMode]);

  // Fetch full project state at the change's revision date (EditChange mode)
  // Uses GET /api/projects/one/full?id=X&revisionDate=Y which returns the complete state
  const shouldFetchHistorical =
    isEditChangeMode &&
    !!changeToEdit &&
    (changeToEdit.type === ProjectChangeType.Info ||
      changeToEdit.type === ProjectChangeType.Billing);

  const { data: historicalProjectData, isLoading: isLoadingHistorical } =
    useProjectDetailsByRevisionDate({
      params: {
        projectId: Number(projectId),
        revisionDate: changeToEdit?.startDate ? parseISO(changeToEdit.startDate) : new Date(0),
      },
      enabled: shouldFetchHistorical && !!changeToEdit?.startDate,
    });

  // Mutation for patching a project change (EditChange mode)
  const patchChangeMutation = usePatchProjectChange({
    onSuccess: () => {
      // Allow navigation to proceed (bypasses unsaved changes guard)
      allowNavigationRef.current?.();
      // Navigate back to changes tab after successful update
      navigate(`/projects/${projectId}?tab=${ProjectTab.Changes}`);
    },
  });

  // Override mode and currentTab when in EditChange mode
  const mode = isEditChangeMode ? FeatureMode.EditChange : originalMode;
  const currentTab = useMemo(() => {
    if (isEditChangeMode && changeToEdit) {
      return changeToEdit.type === ProjectChangeType.Info ? ProjectTab.Info : ProjectTab.Billing;
    }
    return originalCurrentTab;
  }, [isEditChangeMode, changeToEdit, originalCurrentTab]);

  // Extract original change date range for conflict detection (EditChange mode only)
  const originalChangeRange = useMemo(() => {
    if (!isEditChangeMode || !changeToEdit) return undefined;
    return {
      startDate: changeToEdit.startDate,
      endDate: changeToEdit.endDate,
    };
  }, [isEditChangeMode, changeToEdit]);

  // Build effective project data for EditChange mode
  // The revision date endpoint returns a full Project at that point in time,
  // so no manual field merging is needed
  const effectiveProjectData = useMemo(() => {
    if (!isEditChangeMode || !changeToEdit || !projectData) {
      return projectData;
    }

    // Integration changes are non-versioned, use current data
    if (changeToEdit.type === ProjectChangeType.Integration) {
      return projectData;
    }

    // Show loading state while fetching historical data
    if (isLoadingHistorical) return undefined;

    // If no historical data returned, fall back to current data
    // This can happen when the change is currently active
    if (!historicalProjectData) return projectData;

    // Use the historical snapshot directly - it's already a full Project
    return historicalProjectData;
  }, [isEditChangeMode, changeToEdit, projectData, historicalProjectData, isLoadingHistorical]);

  // Determine disabled tabs for EditChange mode
  const disabledTabs = useMemo(() => {
    if (!isEditChangeMode || !changeToEdit) return [];

    const allTabs = [
      ProjectTab.Info,
      ProjectTab.Billing,
      ProjectTab.Integrations,
      ProjectTab.Changes,
    ];
    const activeTab =
      changeToEdit.type === ProjectChangeType.Info ? ProjectTab.Info : ProjectTab.Billing;

    return allTabs.filter(tab => tab !== activeTab);
  }, [isEditChangeMode, changeToEdit]);

  // Handler to refetch project data
  const handleRefetch = () => {
    queryClient.invalidateQueries({ queryKey: ['project', projectId] });
  };

  // Check if all integrations are connected
  const hasIntegrationsTabWarning = Boolean(
    mode === FeatureMode.Edit &&
      projectData?.integrations.integrations.some(integration => !integration.selectedValue?.id)
  );

  // Tab validation state management
  const { tabValid, handleSetTabValid } = useTabValidation([
    ProjectTab.Info,
    ProjectTab.Billing,
    ProjectTab.Integrations,
    ProjectTab.Changes,
  ]);

  // Cancel handler for EditChange mode
  const handleCancelEditChange = useCallback(() => {
    navigate(`/projects/${projectId}?tab=${ProjectTab.Changes}`);
  }, [navigate, projectId]);

  // Load project dependencies (industries, project managers, custom attributes, currencies, organization defaults)
  const {
    isLoading: isLoadingDependencies,
    currencies,
    currencyError,
    organizationDefaults,
  } = useProjectDependencies();

  // Draft management
  const { saveDraftData, getDraftData, clearDraft, hasDraftData, getFinalSubmissionData } =
    useProjectDraft();

  // Get current tab draft data
  const currentTabDraftData = getDraftData(currentTab);

  // Get form state change handler from template
  // Wraps the template's handler to also track hasOnlyNonVersionedChanges
  const handleFormStateChangeHandlerReady = useCallback(
    (handler: (isDirty: boolean, hasChanges?: boolean, isValid?: boolean) => void) => {
      setFormStateChangeHandler(
        () =>
          (
            isDirty: boolean,
            hasChanges?: boolean,
            isValid?: boolean,
            onlyNonVersioned?: boolean
          ) => {
            // Call the original template handler with all form state params
            handler(isDirty, hasChanges, isValid);
            // Also update our local state for non-versioned changes tracking
            setHasOnlyNonVersionedChanges(onlyNonVersioned ?? false);
          }
      );
    },
    []
  );

  // Get wrapped tab change handler from template
  // This handler includes unsaved changes protection
  const [wrappedTabChangeHandler, setWrappedTabChangeHandler] = useState<
    ((tab: string, additionalParams?: Record<string, string>) => void) | null
  >(null);

  const handleTabChangeHandlerReady = useCallback(
    (handler: (tab: string, additionalParams?: Record<string, string>) => void) => {
      setWrappedTabChangeHandler(() => handler);
    },
    []
  );

  // Get allowNavigation function from template's unsaved changes guard
  // Used to bypass guard after successful save operations in EditChange mode
  const handleAllowNavigationReady = useCallback((allowNavigation: () => void) => {
    allowNavigationRef.current = allowNavigation;
  }, []);

  // Wrapper to apply organization defaults to project data before saving in create mode
  const handleSaveProjectWithDefaults = useCallback(
    (params: {
      data: ProjectFormData | (Partial<ProjectFormData> & { id: string });
      successCallback?: (result: PatchEntityResult) => void;
      errorCallback?: (error: unknown) => void;
    }) => {
      let dataToSave: ProjectFormData | (Partial<ProjectFormData> & { id: string }) = params.data;

      // Apply organization defaults in create mode for missing billing fields
      if (mode === FeatureMode.Create && organizationDefaults) {
        const fullData = dataToSave as ProjectFormData;
        dataToSave = {
          ...fullData,
          // Apply default currency if not set
          currency: fullData.currency || organizationDefaults.currency.code,
          // Apply default rate source if not set
          rateSource: fullData.rateSource || organizationDefaults.projectRateSource,
          // Set project budget to 0 if not specified
          projectBudget: fullData.projectBudget ?? 0,
        };
      }

      handleSaveProject({
        data: dataToSave,
        successCallback: params.successCallback,
        errorCallback: params.errorCallback,
      });
    },
    [mode, organizationDefaults, handleSaveProject]
  );

  const [patchResultToDisplay, setPatchResultToDisplay] = useState<PatchEntityResult | null>(null);

  /**
   * Direct save handler for conflict resolution modal integration
   *
   * This handler serves as a bridge between the conflict resolution modal and the project save flow.
   * It's triggered when the user resolves conflicts in the modal by selecting a snapshot and choosing
   * effective dates for the change.
   *
   * Flow:
   * 1. User makes changes to project info/billing that conflict with existing changes
   * 2. Conflict resolution modal opens, showing overlapping snapshots
   * 3. User selects a snapshot to keep and specifies effective date range
   * 4. Modal calls this handler with the merged data and selected dates
   * 5. This handler formats dates and adds them to the payload
   * 6. The save mutation (handleSaveProjectWithDefaults) includes the dates in the API request
   * 7. Backend creates/updates the change with the specified effective date range
   *
   * The effective dates determine:
   * - effectiveStartDate: When this change starts applying
   * - effectiveEndDate: When this change stops applying (null = indefinite)
   *
   * Used by:
   * - ProjectInfoTab: Passed via onDirectSave prop for info changes
   * - ProjectBillingTab: Passed via onDirectSave prop for billing changes
   *
   * @param data - The form data to save (merged with selected snapshot data from conflict resolution)
   * @param effectiveStartDate - The effective start date for the change (from conflict resolution modal)
   * @param effectiveEndDate - Optional effective end date for the change (null = no end date)
   * @returns Promise that resolves when save succeeds, used by modal to close on success
   */
  const handleDirectSave = useCallback(
    async (
      data: ProjectFormData,
      effectiveStartDate?: Date,
      effectiveEndDate?: Date | null
    ): Promise<void> => {
      // In EditChange mode, use patch change mutation to update the existing change
      if (isEditChangeMode && changeToEdit) {
        // Ensure we have effective dates - use change dates as fallback
        const startDate = effectiveStartDate || new Date(changeToEdit.startDate);

        // Determine end date: use provided value, fallback to change's end date, or null
        let endDate: Date | null = null;
        if (effectiveEndDate !== undefined) {
          endDate = effectiveEndDate;
        } else if (changeToEdit.endDate) {
          endDate = new Date(changeToEdit.endDate);
        }

        return new Promise((resolve, reject) => {
          patchChangeMutation.mutate(
            {
              changeId: Number(changeId),
              projectId: Number(projectId),
              data,
              effectiveStartDate: startDate,
              effectiveEndDate: endDate,
              changeType: changeToEdit.type,
            },
            {
              onSuccess: () => resolve(),
              onError: error => reject(error),
            }
          );
        });
      }

      // Regular mode (not EditChange) - use normal project save flow
      return new Promise((resolve, reject) => {
        // Format dates to YYYY-MM-DD format for API
        const formattedStartDate = effectiveStartDate
          ? formatDateToNonISOFormat(effectiveStartDate)
          : undefined;
        const formattedEndDate =
          effectiveEndDate !== null && effectiveEndDate !== undefined
            ? formatDateToNonISOFormat(effectiveEndDate)
            : effectiveEndDate; // Keep null as null

        // Add the effective dates to the data payload
        const dataWithDates = {
          ...data,
          effectiveStartDate: formattedStartDate,
          effectiveEndDate: formattedEndDate,
        };

        handleSaveProjectWithDefaults({
          data: dataWithDates,
          successCallback: (patchResult: PatchEntityResult) => {
            setPatchResultToDisplay(patchResult);
            resolve();
          },
          errorCallback: (error: unknown) => reject(error),
        });
      });
    },
    [
      isEditChangeMode,
      changeToEdit,
      changeId,
      projectId,
      patchChangeMutation,
      handleSaveProjectWithDefaults,
      setPatchResultToDisplay,
    ]
  );

  // Mapping from tab to change type for scheduled change alerts
  const TAB_TO_CHANGE_TYPE: Partial<Record<ProjectTab, ProjectChangeType>> = useMemo(
    () => ({
      [ProjectTab.Info]: ProjectChangeType.Info,
      [ProjectTab.Billing]: ProjectChangeType.Billing,
    }),
    []
  );

  // Handler to navigate to Changes tab with pre-applied filters for a scheduled change
  const handleShowScheduledChange = useCallback(
    (changeType: ProjectChangeType, startDate: string) => {
      const formattedDate = formatDisplayDate(startDate);
      const dateRangeParam = `${formattedDate}:${formattedDate}`;

      const tabChangeHandler = wrappedTabChangeHandler || handleTabChange;
      tabChangeHandler(ProjectTab.Changes, {
        changeType,
        dateRange: dateRangeParam,
      });
    },
    [wrappedTabChangeHandler, handleTabChange]
  );

  // Helper function to wrap tab content with archived alert and edit change alert when needed
  const wrapWithAlert = useCallback(
    (content: React.ReactNode, tab: ProjectTab) => {
      const alerts: React.ReactNode[] = [];

      // Add archived alert if needed
      if (effectiveProjectData?.info.status === ProjectStatus.Archived) {
        alerts.push(
          <ArchivedEntityAlert
            key="archived"
            message={t('authenticated.projects:details.archivedProjectMessage')}
          />
        );
      }

      // Add scheduled archivation alert if project is scheduled to be archived
      if (!isEditChangeMode && effectiveProjectData?.nextArchiveDate) {
        const formattedDate = formatDisplayDate(effectiveProjectData.nextArchiveDate);
        alerts.push(
          <ScheduledArchivationAlert
            key="scheduledArchive"
            message={t('authenticated.projects:scheduledArchive.willBeArchivedOn', {
              date: formattedDate,
            })}
            className="mb-4"
          />
        );
      }

      // Add scheduled unarchivation alert if project is scheduled to be unarchived
      if (!isEditChangeMode && effectiveProjectData?.nextUnarchiveDate) {
        const formattedDate = formatDisplayDate(effectiveProjectData.nextUnarchiveDate);
        alerts.push(
          <ScheduledArchivationAlert
            key="scheduledUnarchive"
            message={t('authenticated.projects:scheduledArchive.willBeUnarchivedOn', {
              date: formattedDate,
            })}
            className="mb-4"
          />
        );
      }

      // Add edit change alert if in EditChange mode and on Info/Billing tab
      if (isEditChangeMode && (tab === ProjectTab.Info || tab === ProjectTab.Billing)) {
        alerts.push(
          <EditChangeAlert
            key="editChange"
            message={t('authenticated.projects:details.tabs.changes.editChangeMessage')}
          />
        );
      }

      // Add scheduled change alert if there's a future change for this tab's type.
      // Skip if the next change is the scheduled archivation/unarchivation — those are
      // already surfaced by ScheduledArchivationAlert above.
      if (!isEditChangeMode) {
        const changeType = TAB_TO_CHANGE_TYPE[tab];
        if (changeType) {
          const nextChange = nextChangeByType[changeType];
          const isArchivationChange =
            nextChange?.startDate &&
            (nextChange.startDate.slice(0, 10) ===
              effectiveProjectData?.nextArchiveDate?.slice(0, 10) ||
              nextChange.startDate.slice(0, 10) ===
                effectiveProjectData?.nextUnarchiveDate?.slice(0, 10));
          if (nextChange && !isArchivationChange) {
            alerts.push(
              <ScheduledChangeAlert
                key="scheduledChange"
                date={formatDisplayDate(nextChange.startDate)}
                onShowChange={() =>
                  handleShowScheduledChange(nextChange.type, nextChange.startDate)
                }
              />
            );
          }
        }
      }

      if (alerts.length > 0) {
        return (
          <div className="space-y-4">
            {alerts}
            {content}
          </div>
        );
      }

      return content;
    },
    [
      effectiveProjectData?.info.status,
      effectiveProjectData?.nextArchiveDate,
      effectiveProjectData?.nextUnarchiveDate,
      isEditChangeMode,
      t,
      TAB_TO_CHANGE_TYPE,
      nextChangeByType,
      handleShowScheduledChange,
    ]
  );

  const tabs = useMemo(() => {
    const allowedTabs: FeatureTab<ProjectFormData>[] = [];
    allowedTabs.push({
      id: ProjectTab.Info,
      label: t('authenticated.projects:details.tabs.info.title'),
      icon: Info,
      hasError: !tabValid.info,
      ref: projectInfoFormRef,
      content: wrapWithAlert(
        <ProjectInfoTab
          ref={projectInfoFormRef}
          mode={mode}
          featureData={effectiveProjectData}
          draftData={mode === FeatureMode.Create ? currentTabDraftData : undefined}
          onFormStateChange={formStateChangeHandler || fallbackFormStateChangeHandler}
          onDirectSave={handleDirectSave}
          originalChangeRange={originalChangeRange}
        />,
        ProjectTab.Info
      ),
    });

    if (isManageProjectBillingEnabled) {
      allowedTabs.push({
        id: ProjectTab.Billing,
        label: t('authenticated.projects:details.tabs.billing.title'),
        icon: CircleDollarSign,
        hasError: !tabValid.billing,
        ref: projectBillingFormRef,
        content: wrapWithAlert(
          <ProjectBillingTab
            ref={projectBillingFormRef}
            mode={mode}
            featureData={effectiveProjectData}
            draftData={
              mode === FeatureMode.Create
                ? (currentTabDraftData as Partial<ProjectBillingFormData>)
                : undefined
            }
            onFormStateChange={formStateChangeHandler || fallbackFormStateChangeHandler}
            onTabChange={wrappedTabChangeHandler || handleTabChange}
            currencies={currencies || []}
            isLoadingCurrencies={isLoadingDependencies}
            currencyError={currencyError}
            defaultOrgCurrency={organizationDefaults?.currency.code}
            defaultRateSource={organizationDefaults?.projectRateSource}
            onDirectSave={handleDirectSave}
            originalChangeRange={originalChangeRange}
          />,
          ProjectTab.Billing
        ),
      });
    }

    allowedTabs.push({
      id: ProjectTab.Integrations,
      label: t('authenticated.projects:details.tabs.integrations.title'),
      icon: CloudCog,
      hasError: !tabValid.integrations,
      ref: projectIntegrationsFormRef,
      hasWarning: hasIntegrationsTabWarning,
      content: wrapWithAlert(
        <ProjectIntegrationsTab
          ref={projectIntegrationsFormRef}
          mode={mode}
          featureData={effectiveProjectData}
          rawIntegrations={rawIntegrations}
          draftData={currentTabDraftData}
          onFormStateChange={formStateChangeHandler || fallbackFormStateChangeHandler}
        />,
        ProjectTab.Integrations
      ),
    });

    if (mode !== FeatureMode.Create) {
      allowedTabs.push({
        id: ProjectTab.Changes,
        label: t('authenticated.projects:details.tabs.changes.title'),
        icon: RefreshCw,
        hasError: !tabValid.changes,
        ref: { current: null } as React.RefObject<null>,
        content: wrapWithAlert(
          <ProjectChangesTab projectData={effectiveProjectData} mode={mode} />,
          ProjectTab.Changes
        ),
        maxWidth: 'max-w-6xl' as const,
      });
    }
    return allowedTabs;
  }, [
    mode,
    t,
    effectiveProjectData,
    currentTabDraftData,
    formStateChangeHandler,
    fallbackFormStateChangeHandler,
    currencies,
    currencyError,
    organizationDefaults,
    isLoadingDependencies,
    isEditChangeMode,
    rawIntegrations,
    projectInfoFormRef,
    projectBillingFormRef,
    projectIntegrationsFormRef,
    wrapWithAlert,
    handleDirectSave,
    hasIntegrationsTabWarning,
    handleTabChange,
    wrappedTabChangeHandler,
    tabValid,
    isManageProjectBillingEnabled,
  ]);

  // OPS-1361: redirect to the default tab when the URL tab is permission-gated
  const allowedTabIds = useMemo(() => tabs.map(t => t.id as ProjectTab), [tabs]);
  useEffect(() => {
    if (
      mode !== FeatureMode.Create &&
      !isEditChangeMode &&
      allowedTabIds.length > 0 &&
      !allowedTabIds.includes(currentTab as ProjectTab)
    ) {
      handleTabChange(ProjectTab.Info);
    }
  }, [currentTab, allowedTabIds, mode, isEditChangeMode, handleTabChange]);

  // Custom validation logic for hasAnyData in create mode
  const calculateHasAnyData = useCallback(
    (context: {
      mode: FeatureMode;
      currentTab: string;
      tabs: FeatureTab<ProjectFormData>[];
      getDraftData: (tab: string) => Partial<ProjectFormData> | undefined;
      hasDraftData: boolean;
    }) => {
      if (context.mode === FeatureMode.Create) {
        // For create mode, only project name is required
        const infoDraft = context.getDraftData(ProjectTab.Info);
        const billingDraft = context.getDraftData(ProjectTab.Billing);

        // Get current tab data if tab has changes but not yet saved to draft
        const infoTab = context.tabs.find(tab => tab.id === ProjectTab.Info);
        const billingTab = context.tabs.find(tab => tab.id === ProjectTab.Billing);

        const currentInfoData =
          context.currentTab === ProjectTab.Info ? infoTab?.ref.current?.getFormData() : null;
        const currentBillingData =
          context.currentTab === ProjectTab.Billing ? billingTab?.ref.current?.getFormData() : null;

        // Merge draft data with current unsaved data
        const finalInfoData = currentInfoData || infoDraft;
        const finalBillingData = currentBillingData || billingDraft;

        // Only validate required field: project name
        const hasValidName = Boolean(finalInfoData?.name && finalInfoData.name.trim().length > 0);

        // Validate optional fields only if they have values (prevent invalid submissions)
        const hasValidProbability =
          !finalInfoData?.probability ||
          (finalInfoData.probability >= 0 && finalInfoData.probability <= 100);
        const hasValidFlatRate = !finalBillingData?.flatRate || finalBillingData.flatRate >= 0;
        const hasValidProjectBudget =
          !finalBillingData?.projectBudget || finalBillingData.projectBudget >= 0;

        return hasValidName && hasValidProbability && hasValidFlatRate && hasValidProjectBudget;
      }

      // For edit/read mode, let template handle default logic
      return false;
    },
    []
  );

  // Form state change handler
  const [_hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const handleFormStateChange = useCallback((isDirty: boolean) => {
    setHasUnsavedChanges(isDirty);
  }, []);

  // Tab content with form state change handler

  const getStatusText = (status: ProjectStatus) => {
    return getProjectStatusText(status);
  };

  // Top right buttons to make actions related to project (archive/delete)
  const actionButtons: ActionButtonConfig[] = useMemo(() => {
    if (!projectData) return [];

    const buttons: ActionButtonConfig[] = [];

    if (isEditProjectsEnabled) {
      buttons.push({
        label:
          projectData.info.status === ProjectStatus.Archived
            ? t('authenticated.projects:archive.unarchiveProject')
            : t('authenticated.projects:archive.archiveProject'),
        icon: projectData.info.status === ProjectStatus.Archived ? ArchiveRestore : ArchiveIcon,
        onClick: () => setIsArchiveModalOpen(true),
        variant: 'outline' as const,
      });
    }

    if (isDeleteProjectsEnabled) {
      buttons.push({
        label: t('authenticated.projects:delete.deleteProjectsButton_one'),
        icon: Trash2,
        onClick: () => setIsDeleteModalOpen(true),
        variant: 'outlineDestructive' as const,
      });
    }

    return buttons;
  }, [projectData, t, isEditProjectsEnabled, isDeleteProjectsEnabled]);

  const footerProps = useMemo(
    () => ({
      labels: {
        saving: t('authenticated.projects:details.tabs.info.saving'),
        create: t('authenticated.projects:details.tabs.info.createProject'),
        saveChanges: t('authenticated.projects:details.tabs.info.saveChanges'),
        dateOfChange: t('authenticated.projects:details.dateOfChange'),
        selectChangeDate: t('authenticated.projects:details.selectChangeDate'),
        updateRecord: t('authenticated.projects:details.tabs.changes.updateRecord'),
        cancel: t('common:cancel'),
      },
      // Hide date picker on Integrations tab (never needs conflict detection)
      showDatePicker: currentTab !== ProjectTab.Integrations,
      // Disable date picker when only non-versioned fields changed (Info tab)
      // This keeps the picker visible but in disabled "Select dates" state
      isDatePickerDisabled: currentTab === ProjectTab.Info && hasOnlyNonVersionedChanges,
      selectedDate: changeToEdit?.startDate,
      selectedEndDate: changeToEdit?.endDate ?? null,
      // A versioned change cannot take effect before the project start date —
      // block earlier dates in the picker so the backend never rejects the save
      // (OPS-1522). No backend "earliest allowed date" exists, so project start
      // date is the lower bound.
      minDate: effectiveProjectData?.info.projectStartDate
        ? parseISO(effectiveProjectData.info.projectStartDate)
        : undefined,
    }),
    [
      t,
      currentTab,
      changeToEdit?.startDate,
      changeToEdit?.endDate,
      hasOnlyNonVersionedChanges,
      effectiveProjectData?.info.projectStartDate,
    ]
  );

  const projectsForDelete = useMemo(() => {
    if (!projectData) return [];
    return [projectData];
  }, [projectData]);

  const modals = useMemo(
    () => (
      <>
        {/* Archive/Unarchive Modal - Externally controlled */}
        {mode === FeatureMode.Edit &&
          isEditProjectsEnabled &&
          projectData &&
          (projectData.info.status === ProjectStatus.Archived ? (
            <UnarchiveProjectModal
              project={projectData}
              open={isArchiveModalOpen}
              onOpen={() => setIsArchiveModalOpen(true)}
              onClose={() => setIsArchiveModalOpen(false)}
            />
          ) : (
            <ArchiveProjectModal
              project={projectData}
              open={isArchiveModalOpen}
              onOpen={() => setIsArchiveModalOpen(true)}
              onClose={() => setIsArchiveModalOpen(false)}
            />
          ))}

        {/* Delete Modal - Externally controlled */}
        {isDeleteProjectsEnabled && projectData && (
          <DeleteProjectsModal
            projectsForDelete={projectsForDelete}
            onSuccess={() => navigate(AuthenticatedRoutes.Projects)}
            open={isDeleteModalOpen}
            onOpen={() => setIsDeleteModalOpen(true)}
            onClose={() => setIsDeleteModalOpen(false)}
          />
        )}
      </>
    ),
    [
      mode,
      projectData,
      isArchiveModalOpen,
      isDeleteModalOpen,
      projectsForDelete,
      navigate,
      isDeleteProjectsEnabled,
      isEditProjectsEnabled,
    ]
  );

  // Only show project loading error, not historical data errors
  // Historical data can legitimately be null (backend returns empty items when change is active)
  // In that case, we gracefully fallback to current project data
  const combinedError = error;
  const combinedLoading = isLoadingProject || isLoadingDependencies || isLoadingHistorical;

  // Get isCheckingConflicts from the current tab ref (follows the same pattern as isValid)
  const currentTabRef = currentTab === ProjectTab.Info ? projectInfoFormRef : projectBillingFormRef;
  const isCheckingConflicts = currentTabRef.current?.isCheckingConflicts ?? false;

  // Combine loading states for footer button (mutations + conflict checking + patch change)
  const combinedIsLoading = isLoading || isCheckingConflicts || patchChangeMutation.isPending;

  // Guard access — redirect to forbidden when View projects is disabled
  if (projectId !== 'new' && !canViewProjects) {
    return <Navigate to={AuthenticatedRoutes.Forbidden} replace />;
  }

  return (
    <FeatureDetailsTemplate<Project, ProjectFormData>
      entityData={effectiveProjectData}
      statusBadgeType={StatusBadgeType.Project}
      mode={mode as unknown as FeatureMode}
      currentTab={currentTab}
      isLoading={combinedIsLoading}
      isLoadingEntity={combinedLoading}
      error={combinedError}
      tabs={tabs}
      getDraftData={(tab: string) => getDraftData(tab as ProjectTab)}
      saveDraftData={({ tab, data }) => saveDraftData({ tab: tab as ProjectTab, data })}
      clearDraft={clearDraft}
      getFinalSubmissionData={getFinalSubmissionData}
      hasDraftData={hasDraftData}
      setTabValid={handleSetTabValid}
      calculateHasAnyData={context => {
        const result = calculateHasAnyData({
          mode: context.mode,
          currentTab: context.currentTab,
          tabs: context.tabs,
          getDraftData: context.getDraftData,
          hasDraftData: context.hasDraftData,
        });
        return result ?? false;
      }}
      handleSave={handleSaveProjectWithDefaults}
      // Actions
      onBack={() => navigate(AuthenticatedRoutes.Projects)}
      onFormStateChange={handleFormStateChange}
      onTabChange={(tab: string, additionalParams?: Record<string, string>) => {
        // Reset non-versioned changes state when switching tabs
        setHasOnlyNonVersionedChanges(false);
        handleTabChange(tab, additionalParams);
      }}
      onCancelEditChange={isEditChangeMode ? handleCancelEditChange : undefined}
      // Unsaved changes confirmation
      enableUnsavedChangesConfirmation={
        mode === FeatureMode.Edit || mode === FeatureMode.Create || mode === FeatureMode.EditChange
      }
      unsavedChangesConfig={{
        title: t(
          mode === FeatureMode.Create
            ? 'authenticated.projects:details.confirmations.discardCreation.title'
            : 'authenticated.projects:details.confirmations.discardChanges.title'
        ),
        message: t(
          mode === FeatureMode.Create
            ? 'authenticated.projects:details.confirmations.discardCreation.message'
            : 'authenticated.projects:details.confirmations.discardChanges.message'
        ),
        confirmText: t(
          mode === FeatureMode.Create
            ? 'authenticated.projects:details.confirmations.discardCreation.confirm'
            : 'authenticated.projects:details.confirmations.discardChanges.confirm'
        ),
        confirmVariant: 'destructive',
      }}
      onFormStateChangeHandlerReady={handleFormStateChangeHandlerReady}
      onTabChangeHandlerReady={handleTabChangeHandlerReady}
      onAllowNavigationReady={handleAllowNavigationReady}
      onRefetch={handleRefetch}
      // Customization
      pageTitle={t('authenticated.projects:details.projectDetails')}
      createTitle={t('authenticated.projects:details.createProject')}
      loadingMessage={t('authenticated.projects:details.loadingProject')}
      errorMessage={t('common:error.reloadPage')}
      showAvatar={true}
      showStatus={true}
      avatarEditable={true}
      onAvatarChange={handleLogoUpload}
      isUploadingAvatar={isUploadingLogo}
      getStatusText={getStatusText}
      actionButtons={isEditChangeMode ? [] : actionButtons}
      useGenericFooter={true}
      footerProps={footerProps}
      disabledTabs={disabledTabs}
      hideFooter={
        mode === FeatureMode.Read ||
        effectiveProjectData?.info.status === ProjectStatus.Archived ||
        (currentTab === ProjectTab.Changes && !isEditChangeMode)
      }
      modals={modals}
      errorActions={[
        {
          label: t('common:backToProjects'),
          onClick: () => navigate(AuthenticatedRoutes.Projects),
          variant: 'secondary',
        },
      ]}
      patchResultToDisplay={patchResultToDisplay}
    />
  );
}
